import {useEffect, useMemo, useState} from "react";
import {Button, Input, InputGroup, Pagination, RadioTile, RadioTileGroup, Table, toaster, Tooltip, Whisper} from "rsuite";
import {AppPlatform} from "../../../api/artifacts";
import {
  AiOutlineCloseIcon,
  AiOutlineSearchIcon,
  FaPlusIcon,
} from "../../../models/Icons.model";
import {ArtifactsApi} from "../../../services/Artifacts.api";
import NotificationFactory from "../../../shared/Notifications";
import {
  APP_PLATFORM_OPTIONS,
  AppChangelogItem,
  AppChangelogsResponse,
  FIRMWARE_TABLE_HEIGHT,
  PAGE_SIZE,
} from "../Firmwares.model";
import {FRONT_FILTER_PAGE_SIZE, FirmwaresUtils} from "../Firmwares.util";
import AppChangelogModal from "./AppChangelogModal";
import FirmwareDescriptionCell from "./FirmwareDescriptionCell";

const {Column, HeaderCell, Cell} = Table;

const AppChangelogs = () => {
  const [loading, setLoading] = useState(false);
  const [changelogs, setChangelogs] = useState<AppChangelogItem[]>([]);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedChangelog, setSelectedChangelog] = useState<AppChangelogItem | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [platform, setPlatform] = useState<AppPlatform | null>(null);

  const loadChangelogs = async () => {
    setLoading(true);

    try {
      const response = await ArtifactsApi.appChangelogs.getList(1, FRONT_FILTER_PAGE_SIZE) as AppChangelogsResponse;
      setChangelogs(FirmwaresUtils.getItems<AppChangelogItem>(response));
    } catch (error) {
      console.error("Ошибка получения изменений приложения:", error);
      toaster.push(NotificationFactory.error(FirmwaresUtils.getListErrorMessage(error, "изменений приложения")), {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChangelogs().then();
  }, []);

  const handleClearSearch = () => {
    setSearchValue("");
    setPage(1);
  };

  const handlePlatformChange = (value: AppPlatform | null) => {
    setPlatform(value);
    setPage(1);
  };

  const filteredChangelogs = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return changelogs.filter((changelog) => {
      const matchesPlatform = !platform || changelog.platform === platform;
      const matchesSearch = !normalizedSearch || FirmwaresUtils.getSearchString(changelog).toLowerCase().includes(normalizedSearch);

      return matchesPlatform && matchesSearch;
    });
  }, [changelogs, searchValue, platform]);

  const paginatedChangelogs = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;

    return filteredChangelogs.slice(start, start + PAGE_SIZE);
  }, [filteredChangelogs, page]);

  const handleOpenCreateModal = () => {
    setSelectedChangelog(null);
    setModalMode("create");
    setModalOpen(true);
  };

  const handleOpenEditModal = (changelog: AppChangelogItem) => {
    setSelectedChangelog(changelog);
    setModalMode("edit");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedChangelog(null);
  };

  const handleSaveSuccess = () => {
    handleCloseModal();
    loadChangelogs().then();
  };

  return (
    <>
      <div className="row mt-3 align-items-center firmwares-toolbar">
        <div className="col-md-1 col-sm-2 mb-3">
          <Whisper placement="top" speaker={<Tooltip>Добавить изменение приложения</Tooltip>}>
            <Button
              style={{height: 35}}
              size="md"
              appearance="primary"
              color="green"
              onClick={handleOpenCreateModal}
            >
              <FaPlusIcon />
            </Button>
          </Whisper>
        </div>

        <div className="col-md-4 col-sm-10 mb-3">
          <InputGroup>
            <InputGroup.Addon>
              <AiOutlineSearchIcon />
            </InputGroup.Addon>
            <Input
              placeholder="Поиск изменений..."
              value={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                setPage(1);
              }}
            />
            {searchValue && (
              <InputGroup.Button onClick={handleClearSearch}>
                <AiOutlineCloseIcon />
              </InputGroup.Button>
            )}
          </InputGroup>
        </div>

      </div>

      <div className="mb-3">
        <RadioTileGroup
          as="div"
          aria-label="Фильтр по платформе"
          className="w-100 firmwares-platform-filter-group"
          value={platform || "all"}
          onChange={(value) => handlePlatformChange(value === "all" ? null : value as AppPlatform)}
        >
          <RadioTile value="all" label="Все">
            <span className="radio-tile-text">Все</span>
          </RadioTile>

          {APP_PLATFORM_OPTIONS.map((option) => (
            <RadioTile
              key={option.value}
              value={option.value}
              label={option.label}
            >
              <span className="radio-tile-text">{option.label}</span>
            </RadioTile>
          ))}
        </RadioTileGroup>
      </div>

      <Table
        height={FIRMWARE_TABLE_HEIGHT}
        data={paginatedChangelogs}
        loading={loading}
        onRowClick={(rowData: AppChangelogItem) => handleOpenEditModal(rowData)}
        className="users-table firmwares-table"
        rowHeight={60}
        wordWrap
      >
        <Column width={100} sortable verticalAlign="middle" fullText>
          <HeaderCell>Версия</HeaderCell>
          <Cell dataKey="version">
            {(rowData: AppChangelogItem) => rowData.version || "-"}
          </Cell>
        </Column>

        <Column width={120} sortable verticalAlign="middle" fullText>
          <HeaderCell>Платформа</HeaderCell>
          <Cell dataKey="platform">
            {(rowData: AppChangelogItem) => rowData.platform || "-"}
          </Cell>
        </Column>

        <Column width={400} sortable verticalAlign="middle">
          <HeaderCell>Описание</HeaderCell>
          <Cell dataKey="description">
            {(rowData: AppChangelogItem) => <FirmwareDescriptionCell description={rowData.description} />}
          </Cell>
        </Column>

        <Column width={150} sortable verticalAlign="middle" fullText>
          <HeaderCell>Создал</HeaderCell>
          <Cell dataKey="createdBy">
            {(rowData: AppChangelogItem) => rowData.createdBy || "-"}
          </Cell>
        </Column>

        <Column width={150} sortable verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">Создано</HeaderCell>
          <Cell dataKey="createdAt">
            {(rowData: AppChangelogItem) => FirmwaresUtils.formatDate(rowData.createdAt)}
          </Cell>
        </Column>
      </Table>

      <div className="d-flex justify-content-end mt-3">
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          layout={["total", "-", "pager"]}
          total={filteredChangelogs.length}
          limit={PAGE_SIZE}
          activePage={page}
          onChangePage={setPage}
        />
      </div>

      <AppChangelogModal
        open={modalOpen}
        mode={modalMode}
        changelog={selectedChangelog}
        onClose={handleCloseModal}
        onSuccess={handleSaveSuccess}
      />
    </>
  );
};

export default AppChangelogs;
